import type { ReactNode } from 'react';
import './Tabela.css';

export type Coluna<T> = {
  readonly chave: string;
  readonly rotulo: string;
  /** Largura da coluna em `<col>` (px ou %). Sem valor, o navegador distribui. */
  readonly largura?: string;
  readonly alinhamento?: 'esquerda' | 'direita';
  readonly celula: (item: T) => ReactNode;
};

export type SelecaoTabela = {
  readonly selecionados: ReadonlySet<string>;
  readonly aoAlternar: (chave: string) => void;
  readonly aoAlternarTodos: () => void;
};

type Props<T> = {
  readonly itens: readonly T[];
  readonly colunas: readonly Coluna<T>[];
  readonly chaveDe: (item: T) => string;
  /** Rótulo acessível da tabela — o cabeçalho visual nem sempre a descreve. */
  readonly descricao: string;
  readonly selecao?: SelecaoTabela;
  /** Classe extra por linha, para os fundos de falta e sobra do inventário. */
  readonly classeDaLinha?: (item: T) => string | undefined;
  readonly densidade?: 'padrao' | 'confortavel';
  readonly rodape?: ReactNode;
};

/**
 * Tabela densa do painel: cabeçalho fixo de 38px, linhas de 40px e zebra por
 * HOVER — listra alternada compete com os fundos de estado (falta e sobra) que
 * as telas de inventário usam para significar coisa.
 */
export function Tabela<T>({
  itens,
  colunas,
  chaveDe,
  descricao,
  selecao,
  classeDaLinha,
  densidade = 'padrao',
  rodape,
}: Props<T>) {
  const todosSelecionados =
    selecao !== undefined &&
    itens.length > 0 &&
    itens.every((item) => selecao.selecionados.has(chaveDe(item)));

  return (
    <div className="tabela">
      <div className="tabela__rolagem">
        <table className={`tabela__grade tabela__grade--${densidade}`}>
          <caption className="apenas-leitor-de-tela">{descricao}</caption>
          <colgroup>
            {selecao !== undefined && <col style={{ width: '36px' }} />}
            {colunas.map((coluna) => (
              <col
                key={coluna.chave}
                style={coluna.largura === undefined ? undefined : { width: coluna.largura }}
              />
            ))}
          </colgroup>
          <thead>
            <tr>
              {selecao !== undefined && (
                <th scope="col">
                  <input
                    type="checkbox"
                    className="tabela__caixa"
                    checked={todosSelecionados}
                    onChange={selecao.aoAlternarTodos}
                    aria-label="Selecionar todas as linhas"
                  />
                </th>
              )}
              {colunas.map((coluna) => (
                <th
                  key={coluna.chave}
                  scope="col"
                  className={coluna.alinhamento === 'direita' ? 'tabela--direita' : undefined}
                >
                  {coluna.rotulo}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {itens.map((item) => {
              const chave = chaveDe(item);
              const extra = classeDaLinha?.(item);

              return (
                <tr key={chave} className={extra}>
                  {selecao !== undefined && (
                    <td>
                      <input
                        type="checkbox"
                        className="tabela__caixa"
                        checked={selecao.selecionados.has(chave)}
                        onChange={() => {
                          selecao.aoAlternar(chave);
                        }}
                        aria-label={`Selecionar ${chave}`}
                      />
                    </td>
                  )}
                  {colunas.map((coluna) => (
                    <td
                      key={coluna.chave}
                      className={coluna.alinhamento === 'direita' ? 'tabela--direita' : undefined}
                    >
                      {coluna.celula(item)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {rodape !== undefined && <div className="tabela__rodape">{rodape}</div>}
    </div>
  );
}
